import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PlacementSelector.css';

const PlacementSelector = ({ placements = [], onChange, readOnly = false }) => {
    const { t } = useTranslation();
    const [selectedView, setSelectedView] = useState('front'); // 'front' or 'back'

    const bodyParts = {
        front: [
            { name: t('body_part_head'), x: 50, y: 8 },
            { name: t('body_part_neck'), x: 50, y: 15 },
            { name: t('body_part_chest'), x: 50, y: 25 },
            { name: t('body_part_l_arm'), x: 25, y: 30 },
            { name: t('body_part_r_arm'), x: 75, y: 30 },
            { name: t('body_part_l_forearm'), x: 20, y: 45 },
            { name: t('body_part_r_forearm'), x: 80, y: 45 },
            { name: t('body_part_abdomen'), x: 50, y: 40 },
            { name: t('body_part_l_thigh'), x: 40, y: 65 },
            { name: t('body_part_r_thigh'), x: 60, y: 65 },
            { name: t('body_part_l_calf'), x: 40, y: 85 },
            { name: t('body_part_r_calf'), x: 60, y: 85 },
        ],
        back: [
            { name: t('body_part_nuca'), x: 50, y: 12 },
            { name: t('body_part_upper_back'), x: 50, y: 25 },
            { name: t('body_part_lower_back'), x: 50, y: 40 },
            { name: t('body_part_l_shoulder'), x: 30, y: 20 },
            { name: t('body_part_r_shoulder'), x: 70, y: 20 },
        ]
    };

    const handleBodyClick = (e) => {
        if (readOnly || placements.length >= 3) {
            return; // Max 3 placements or ReadOnly
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // Find closest body part
        const currentParts = bodyParts[selectedView];
        let closestPart = currentParts[0];
        let minDistance = Infinity;

        currentParts.forEach(part => {
            const distance = Math.sqrt(Math.pow(part.x - x, 2) + Math.pow(part.y - y, 2));
            if (distance < minDistance) {
                minDistance = distance;
                closestPart = part;
            }
        });

        const newPlacement = {
            bodyPart: closestPart.name,
            position: { x: closestPart.x, y: closestPart.y },
            view: selectedView
        };

        onChange([...placements, newPlacement]);
    };

    const removePlacement = (index) => {
        const newPlacements = placements.filter((_, i) => i !== index);
        onChange(newPlacements);
    };

    const clearAll = () => {
        onChange([]);
    };

    return (
        <div className={`placement-selector ${readOnly ? 'read-only' : ''}`}>
            {!readOnly && (
                <div className="placement-header">
                    <h4>{t('placement_suggestions')}</h4>
                    <p className="placement-instructions">{t('placement_instructions')}</p>
                    <div className="placement-info">
                        <span>{t('placements_added', { count: placements.length })}</span>
                        {placements.length > 0 && (
                            <button type="button" onClick={clearAll} className="clear-btn">
                                {t('clear_placements')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="view-toggle">
                <button
                    type="button"
                    className={selectedView === 'front' ? 'active' : ''}
                    onClick={() => setSelectedView('front')}
                >
                    {t('view_front')}
                </button>
                <button
                    type="button"
                    className={selectedView === 'back' ? 'active' : ''}
                    onClick={() => setSelectedView('back')}
                >
                    {t('view_back')}
                </button>
            </div>

            <div className="body-container">
                <div
                    className="body-silhouette"
                    onClick={handleBodyClick}
                    style={{ cursor: readOnly ? 'default' : (placements.length < 3 ? 'crosshair' : 'not-allowed') }}
                >
                    {/* Simple body outline SVG */}
                    <svg viewBox="0 0 100 100" className="body-svg">
                        {selectedView === 'front' ? (
                            <g>
                                {/* Head */}
                                <circle cx="50" cy="8" r="6" fill="none" stroke="#666" strokeWidth="0.8" />
                                {/* Neck */}
                                <line x1="50" y1="14" x2="50" y2="18" stroke="#666" strokeWidth="3" />
                                {/* Torso */}
                                <rect x="38" y="18" width="24" height="30" fill="none" stroke="#666" strokeWidth="1" rx="2" />
                                {/* Arms - mais grossos */}
                                <line x1="38" y1="22" x2="22" y2="30" stroke="#666" strokeWidth="4" />
                                <line x1="22" y1="30" x2="16" y2="50" stroke="#666" strokeWidth="4" />
                                <line x1="62" y1="22" x2="78" y2="30" stroke="#666" strokeWidth="4" />
                                <line x1="78" y1="30" x2="84" y2="50" stroke="#666" strokeWidth="4" />
                                {/* Legs - mais grossas */}
                                <line x1="44" y1="48" x2="38" y2="75" stroke="#666" strokeWidth="5" />
                                <line x1="38" y1="75" x2="38" y2="95" stroke="#666" strokeWidth="5" />
                                <line x1="56" y1="48" x2="62" y2="75" stroke="#666" strokeWidth="5" />
                                <line x1="62" y1="75" x2="62" y2="95" stroke="#666" strokeWidth="5" />
                            </g>
                        ) : (
                            <g>
                                {/* Head */}
                                <circle cx="50" cy="8" r="6" fill="none" stroke="#666" strokeWidth="0.8" />
                                {/* Neck */}
                                <line x1="50" y1="14" x2="50" y2="18" stroke="#666" strokeWidth="3" />
                                {/* Back */}
                                <rect x="38" y="18" width="24" height="30" fill="none" stroke="#666" strokeWidth="1" rx="2" />
                                {/* Arms - mais grossos */}
                                <line x1="38" y1="22" x2="22" y2="30" stroke="#666" strokeWidth="4" />
                                <line x1="62" y1="22" x2="78" y2="30" stroke="#666" strokeWidth="4" />
                                {/* Legs - mais grossas */}
                                <line x1="44" y1="48" x2="38" y2="95" stroke="#666" strokeWidth="5" />
                                <line x1="56" y1="48" x2="62" y2="95" stroke="#666" strokeWidth="5" />
                            </g>
                        )}
                    </svg>

                    {/* Placement markers */}
                    {placements.map((placement, index) => (
                        placement.view === selectedView && (
                            <div
                                key={index}
                                className="placement-marker"
                                style={{
                                    left: `${placement.position.x}%`,
                                    top: `${placement.position.y}%`
                                }}
                                onClick={(e) => {
                                    if (readOnly) return;
                                    e.stopPropagation();
                                    removePlacement(index);
                                }}
                                title={readOnly ? placement.bodyPart : `${placement.bodyPart} - Click to remove`}
                            >
                                <span className="marker-number">{index + 1}</span>
                            </div>
                        )
                    ))}
                </div>
            </div>

            {/* Placement list */}
            {placements.length > 0 && (
                <div className="placement-list">
                    {placements.map((placement, index) => (
                        <div key={index} className="placement-item">
                            <span className="placement-number">{index + 1}</span>
                            <span className="placement-name">{placement.bodyPart}</span>
                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={() => removePlacement(index)}
                                    className="remove-placement-btn"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PlacementSelector;
